/* eslint-disable-next-line import/no-unresolved */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
/* eslint-disable-next-line import/no-unresolved */
import { useDispatch, useSelector } from 'react-redux';
import { useSummaryInfoByServiceId } from '.'; // 依存関係に注意
import { useSelectionsTableDataMain } from './autoSelectDetails'; // 依存関係に注意
import Logger from '../Logger';
import {
  changeOmakaseInputBudget,
  changeOmakaseInputTerm,
  changeOmakaseInputPolicy,
  findOmakasePlanStrategies,
  setOmakaseCreatingInputSetCount,
  resetOmakaseCreatingInputSetCount,
  addOmakaseCreatingErrorMessage,
  removeOmakaseCreatingErrorMessageByMessage,
  removeOmakaseCreatingErrorMessageByInputName,
  changeOmakaseCreatingSimulationTermId,
  changeOmakaseCreatingSelectedStrategyId,
  createOmakasePlan,
  initializeOmakaseInput,
  getOmakasePlanMasterRequest,
} from '../../redux/actions/omakasePlanActions';
import {
  FX,
  STRATEGY_SETS_RESULTS,
  VALIDATION_ERROR_EMPTY_FIELD,
  VALIDATION_ERROR_EMPTY_FIELD_OMAKASE_PLAN,
  VALIDATION_ERROR_INVALID_VALUE,
  VALIDATION_ERROR_MAX_BUDGET,
  AP_GROUP_SOURCES,
  VALIDATION_ERROR_SET_NUMBER_OUT_OF_RANGE,
  VALIDATION_ERROR_MIN_STRATEGY_REQUIRED,
} from '../../constants';
import { useCalculatingChartData } from '../../hooks';
import { roundRoi, roundUpBy1000 } from '../../utils';
import { MOBILE_AUTO_SELECT_PRICE_CHART_URL } from '../../config';
import { getRunNowErrorMessageByErrorCode, RUN_NOW_ERROR_MESSAGES } from '../../constants/errorMesages';
import { checkIsWebApp } from '..';
import { NEXT_BUTTON_TEXT, OMAKASE_PLAN_START_TEXT, RESET_SET_COUNT_TEXT } from '../../constants/omakasePlan';
import { getParentTag } from '../../utils/select';

export const useSelectedChildTagConfig = (parentTagId, childTagId) => {
  const selectionTags = useSelector((state) => state.autoSelect.selectionTags);
  return getParentTag(selectionTags, parentTagId)?.children?.find((child) => child.tagId === childTagId);
};

export const useAutoSelectVisibleFlg = (parentTagId, childTagId) => {
  const foundTag = useSelectedChildTagConfig(parentTagId, childTagId);
  return foundTag?.autoSelectVisibleFlg || false;
};

const useMinMarginRecommend = (omakasePlanName) => {
  const master = useSelector((state) => state.omakasePlan.omakasePlanMaster);
  const omakasePlan = master[omakasePlanName] || [];
  return omakasePlan.reduce((acc, curr) => {
    const { margin_recommend: value } = curr;
    if (acc == null) return value;
    return Math.min(value, acc);
  }, null);
};

const useSelectedOmakasePlan = (omakasePlanName, budget) => {
  const master = useSelector((state) => state.omakasePlan.omakasePlanMaster);
  const omakasePlan = master[omakasePlanName] || [];
  return omakasePlan
    .sort((a, b) => {
      const { margin_recommend: valueA } = a;
      const { margin_recommend: valueB } = b;
      return valueB - valueA; // 降順
    })
    .find(({ margin_recommend: value }) => budget * 10000 >= value);
};

const createMinError = (value, minMarginRecommend) => {
  if (value < minMarginRecommend) {
    // 指定条件ではあとxx万円の予算が必要です。
    return Math.ceil((minMarginRecommend - value) / 10000);
  }
  return 0;
};

const calcDefaultBudget = (minMarginRecommend, orderableMargin) => {
  /* #9
    予算のデフォルト値は、"予算を入力"にて表示する金額は「期間・方針」の組み合わせにおける推奨証拠金の最低金額を
    5万円単位で切り上げした金額、または50万円、(顧客の発注可能額または1000万円のいずれか小さい方)のいずれか高い方であること
    */
  if (!minMarginRecommend) {
    return 0;
  }
  const base1 = 10000; // 1万
  const base5 = 50000; // 5万
  const roundMinMargin = Math.ceil(minMarginRecommend / base5) * base5;
  const orderableMax = Math.min(orderableMargin, 1000 * base1); // 1000万
  const max = Math.max(
    roundMinMargin,
    50 * base1, // 50万
    orderableMax,
  );
  return Math.floor(max / base1);
};

// triggerがtruthyになったときのorderableMarginを返す
const useOrderableMargin = (trigger) => {
  const serviceId = FX;
  const tempOrderableMargin = useSummaryInfoByServiceId(serviceId)?.orderableMargin ?? 0;
  const orderableMarginRef = useRef(tempOrderableMargin);
  const triggerRef = useRef(false);

  if (triggerRef.current !== !!trigger) {
    triggerRef.current = !!trigger;
    orderableMarginRef.current = tempOrderableMargin;
  }
  return orderableMarginRef.current;
};

const useIsSameInput = () => {
  const inputForm = useSelector((state) => state.omakasePlan.inputForm);
  const input = useSelector((state) => state.omakasePlan.creating.input);
  const isSame = (a, b) => a && b && a === b;
  return (
    isSame(inputForm?.budget, input?.budget) &&
    isSame(inputForm?.term, input?.term) &&
    isSame(inputForm?.policy, input?.policy)
  );
};

const createErrorObj = (inputName, errorMessage) => ({ inputName: `${inputName}`, errorMessage });

const getOmakasePlanName = ({ term, policy }) => {
  return `${term}-${policy}`;
};

export const OmakasePlanStatusCheckType = {
  START: 1,
  INPUT: 2,
  SELECTED: 3,
  CREATING: 4,
};

export const useOmakasePlanStatusCheck = ({ type }) => {
  const omakasePlan = useSelector((state) => state.omakasePlan);
  const {
    omakasePlanMaster,
    inputForm,
    creating,
    portfolios,
    loading,
    arePortfoliosLoading,
    isOmakasePlanMasterLoading,
  } = omakasePlan;

  const isReadyOmakasePlanMaster =
    !isOmakasePlanMasterLoading && omakasePlanMaster && Object.keys(omakasePlanMaster).length > 0;
  const isReadyPortfolios = !arePortfoliosLoading && portfolios && portfolios.length > 0;

  const omakasePlanName = getOmakasePlanName(inputForm);
  const hasOmakasePlan = omakasePlanMaster[omakasePlanName]?.length > 0;

  const hasResult = Object.keys(creating.input).length > 0 && creating.strategyList.length > 0;

  if (type === OmakasePlanStatusCheckType.START) {
    return isReadyPortfolios;
  }
  if (type === OmakasePlanStatusCheckType.INPUT) {
    return isReadyOmakasePlanMaster && isReadyPortfolios;
  }
  if (type === OmakasePlanStatusCheckType.SELECTED) {
    return hasOmakasePlan;
  }
  if (type === OmakasePlanStatusCheckType.CREATING) {
    return !loading && hasResult;
  }

  return null;
};

export const useOmakasePlanMasterLoad = () => {
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.omakasePlan.isOmakasePlanMasterLoading);
  const isReadyStart = useOmakasePlanStatusCheck({ type: OmakasePlanStatusCheckType.START });
  const onClick = useCallback(
    ({ successCallback, failureCallback }) => {
      dispatch(initializeOmakaseInput());
      dispatch(getOmakasePlanMasterRequest({ successCallback, failureCallback }));
    },
    [dispatch],
  );
  return useMemo(
    () => ({
      isLoading,
      isReadyStart,
      onClick,
    }),
    [isLoading, onClick, isReadyStart],
  );
};

export const useOmakasePlanInput = ({ isFocused = false } = {}) => {
  const dispatch = useDispatch();
  const inputForm = useSelector((state) => state.omakasePlan.inputForm);
  const strategyList = useSelector((state) => state.omakasePlan.creating.strategyList);
  const { budget, term, policy } = inputForm;
  const omakasePlanName = getOmakasePlanName(inputForm);
  const minMarginRecommend = useMinMarginRecommend(omakasePlanName);
  const selectedOmakasePlan = useSelectedOmakasePlan(omakasePlanName, budget);

  const isReady = useOmakasePlanStatusCheck({ type: OmakasePlanStatusCheckType.INPUT }) && isFocused;
  const orderableMargin = useOrderableMargin(isReady);
  const defaultBudget = calcDefaultBudget(minMarginRecommend, orderableMargin);

  const [manualEdited, setManualEdited] = useState(false);
  const isReadySelected = useOmakasePlanStatusCheck({ type: OmakasePlanStatusCheckType.SELECTED });

  const setBudget = useCallback(
    (value) => {
      dispatch(changeOmakaseInputBudget({ budget: value }));
      setManualEdited(true); // ユーザが直接入力済み
    },
    [dispatch],
  );
  const setTerm = useCallback(
    (value) => {
      dispatch(changeOmakaseInputTerm({ term: value }));
    },
    [dispatch],
  );
  const setPolicy = useCallback(
    (value) => {
      dispatch(changeOmakaseInputPolicy({ policy: value }));
    },
    [dispatch],
  );
  const [budgetError, setBudgetError] = useState(null);
  const [budgetMinError, setBudgetMinError] = useState(null);
  const validateBudget = useCallback(
    (value) => {
      const valueNum = Number(value);
      const minValue = minMarginRecommend;
      if (!valueNum) {
        setBudgetError(VALIDATION_ERROR_EMPTY_FIELD); // 必須項目です
        setBudgetMinError(null);
      } else if (valueNum > 1000) {
        setBudgetError(VALIDATION_ERROR_MAX_BUDGET); // 1,000万円以下で入力してください。
        setBudgetMinError(null);
      } else if (minValue && valueNum * 10000 < minValue) {
        setBudgetError(null);
        setBudgetMinError(createMinError(valueNum * 10000, minValue));
      } else {
        setBudgetError(null);
        setBudgetMinError(null);
      }
    },
    [minMarginRecommend],
  );
  const hasError = !!(budgetError || budgetMinError);

  const handleCreate = useCallback(() => {
    if (!selectedOmakasePlan) {
      Logger.error('find omakasePlan failed.');
      return;
    }
    dispatch(findOmakasePlanStrategies({ selectedOmakasePlan }));
  }, [dispatch, selectedOmakasePlan]);
  const isWebApp = checkIsWebApp();
  const isSameInput = useIsSameInput();
  const created = strategyList && strategyList.length > 0;
  const isDisabled = hasError || !isReadySelected || (isWebApp && created && isSameInput);

  const rerunValidateRef = useRef(null);
  rerunValidateRef.current = { validateBudget, budget };

  useEffect(() => {
    rerunValidateRef.current?.validateBudget?.(rerunValidateRef.current?.budget);
  }, [minMarginRecommend]);

  useEffect(() => {
    if (isFocused && defaultBudget && !manualEdited) {
      dispatch(changeOmakaseInputBudget({ budget: defaultBudget }));
      rerunValidateRef.current?.validateBudget?.(defaultBudget);
    }
  }, [dispatch, isFocused, defaultBudget, manualEdited]);

  return useMemo(() => {
    return {
      budget: {
        get: budget,
        set: setBudget,
        validation: validateBudget,
        name: 'budget',
        error: budgetError ? [createErrorObj('budget', budgetError)] : [],
      },
      term: {
        get: term,
        set: setTerm,
      },
      policy: {
        get: policy,
        set: setPolicy,
      },
      createBtn: {
        label: created && isWebApp ? '再作成する' : '作成する',
        onClick: handleCreate,
        isDisabled,
      },
      omakasePlanName,
      selectedOmakasePlan,
      budgetMinError,
      hasError,
      created,
    };
  }, [
    budget,
    term,
    policy,
    setBudget,
    setTerm,
    setPolicy,
    validateBudget,
    budgetError,
    omakasePlanName,
    selectedOmakasePlan,
    budgetMinError,
    hasError,
    handleCreate,
    isDisabled,
    created,
    isWebApp,
  ]);
};

const filterHasSetCount = (strategyList) => strategyList.filter((strategy) => strategy.strategySets > 0);

export const useMargeOmakasePlanStrategiesSetCount = ({ filtering = false, simulationTermId } = {}) => {
  const currentStrategyList = useSelector((state) => state.omakasePlan.creating.strategyList);
  const selectedSimulationTermId = useSelector((state) => state.omakasePlan.creating.selectedSimulationTermId);
  const cached =
    useSelector(
      (state) => state.omakasePlan.creating.strategyListCache[simulationTermId || selectedSimulationTermId],
    ) || [];
  const strategyList = simulationTermId ? cached : currentStrategyList;

  const inputSetCount = useSelector((state) => state.omakasePlan.creating.inputSetCount) || {};

  const inputSetCountJSON = JSON.stringify(inputSetCount);

  return useMemo(() => {
    const inputSetCountObj = JSON.parse(inputSetCountJSON);
    const merged = strategyList.reduce((acc, strategy) => {
      const { strategyId } = strategy || {};
      const strategySets = Number(inputSetCountObj[strategyId]) || 0;
      acc.push({
        ...strategy,
        strategySets,
      });
      return acc;
    }, []);
    if (filtering) {
      // セット数ありのものだけ
      return filterHasSetCount(merged);
    }
    return merged;
  }, [strategyList, inputSetCountJSON, filtering]);
};

export const useOmakasePlanStrategies = () => {
  const dispatch = useDispatch();
  const strategyList = useMargeOmakasePlanStrategiesSetCount();
  const handleResetSetCount = useCallback(() => {
    dispatch(resetOmakaseCreatingInputSetCount());
  }, [dispatch]);
  const { errorMessages } = useSelector((state) => state.omakasePlan.creating);
  const hasError = errorMessages.length > 0;

  return useMemo(() => {
    return {
      strategyList,
      resetSetCountBtn: {
        label: RESET_SET_COUNT_TEXT,
        onClick: handleResetSetCount,
      },
      nextBtn: {
        label: NEXT_BUTTON_TEXT,
        isDisabled: filterHasSetCount(strategyList).length === 0 || hasError,
      },
    };
  }, [strategyList, handleResetSetCount, hasError]);
};

/**
 * #37
 * 運用資金(/推奨証拠金)は期間、セット数に応じて更新する
 * 運用資金(/推奨証拠金)を更新してもカード並び順は変更しない
 *
 * 2.15.0 推奨証拠金(運用資金目安)から発注証拠金に変更
 */
const getSubtotalMarginRequired = (strategy) => {
  const marginRequired = strategy?.strategyDetail?.simulationStats?.marginRequired || 0;
  const strategySets = strategy?.strategySets || 0;
  return roundUpBy1000(marginRequired * strategySets);
};

export const useOmakasePlanStrategy = (strategy, finalStrategyErrorCallback) => {
  const dispatch = useDispatch();
  const strategyId = strategy?.strategyId ?? '';
  const strategyIdString = `${strategyId}`;
  const handleChangeStrategySet = useCallback(
    (value) => {
      if (strategyId) {
        dispatch(setOmakaseCreatingInputSetCount({ strategyId, count: value }));
      }
    },
    [dispatch, strategyId],
  );
  const { inputSetCount, errorMessages } = useSelector((state) => state.omakasePlan.creating);
  const isFinalStrategy = !Object.entries(inputSetCount).find(
    ([key, val]) => key !== strategyIdString && Number(val) > 0,
  );
  const countValue = inputSetCount[strategyId];

  const countValidator = useCallback((value) => {
    if (value === '' || value == null) {
      return [false, VALIDATION_ERROR_EMPTY_FIELD_OMAKASE_PLAN]; // おまかせプラン特有の文言を使用
    }
    if (Number.isNaN(value)) {
      return [false, VALIDATION_ERROR_INVALID_VALUE]; // 他の同様のバリデーションにあわせている
    }
    if (Number(value) > 2000) {
      // TODO: constants
      return [false, VALIDATION_ERROR_SET_NUMBER_OUT_OF_RANGE]; // 他の同様のバリデーションにあわせて、「までで」にしていない
    }
    return [true, null];
  }, []);
  const finalStrategyValidator = useCallback(
    (value) => {
      dispatch(removeOmakaseCreatingErrorMessageByMessage({ errorMessage: VALIDATION_ERROR_MIN_STRATEGY_REQUIRED }));
      if (isFinalStrategy && Number(value) === 0) {
        finalStrategyErrorCallback?.();
        return [false, VALIDATION_ERROR_MIN_STRATEGY_REQUIRED];
      }
      return [true, null];
    },
    [isFinalStrategy, dispatch, finalStrategyErrorCallback],
  );

  const checkedValueRef = useRef(countValue);
  const strategyCountValidator = useCallback(
    (value) => {
      // 同じ値で複数回呼ばれる場合があるのでチェック済みの場合処理しない
      if (value === checkedValueRef.current) {
        return false;
      }
      checkedValueRef.current = value;
      const validators = [countValidator, finalStrategyValidator];
      dispatch(removeOmakaseCreatingErrorMessageByInputName({ inputName: strategyIdString }));
      const isValid = validators.reduce((acc, validator) => {
        if (acc) {
          const [isValidTmp, message] = validator(value);
          if (!isValidTmp && message) {
            dispatch(addOmakaseCreatingErrorMessage(createErrorObj(strategyIdString, message)));
          }
          return isValidTmp;
        }
        return acc;
      }, true);
      if (isValid) {
        dispatch(removeOmakaseCreatingErrorMessageByInputName({ inputName: strategyIdString }));
      }
      return isValid;
    },
    [dispatch, strategyIdString, countValidator, finalStrategyValidator],
  );
  checkedValueRef.current = countValue;

  return useMemo(
    () => ({
      strategy,
      count: {
        name: strategyIdString,
        get: countValue,
        set: handleChangeStrategySet,
        validation: strategyCountValidator,
        error: errorMessages,
      },
      marginRequired: getSubtotalMarginRequired(strategy),
    }),
    [strategy, strategyIdString, countValue, handleChangeStrategySet, strategyCountValidator, errorMessages],
  );
};

const calcRoi = ({ marginRecommended, totalPl }) => {
  const roundedMarginRecommended = roundUpBy1000(marginRecommended);
  return roundRoi((totalPl * 100) / roundedMarginRecommended);
};

/**
 * #49
 * 推奨証拠金はセット数に応じて常に「短期：1年、中期長期：3年」の推奨証拠金合計を表示
 ※ セット数の変更では増減する
 * 例）AUD/NZDの1セット当たり推奨証拠金が20万
 * ⇒2セットなら40万、3セットなら60万（カード内/合計両方）
 * 
 * 2.15.0 推奨証拠金(運用資金目安)から発注証拠金に変更
*/
const useOmakasePlanDefaultMarginRequired = () => {
  const defaultSimulationTermId = useSelector((state) => state.omakasePlan.creating.result?.defaultSimulationTermId);
  const strategyList = useMargeOmakasePlanStrategiesSetCount({
    filtering: true,
    simulationTermId: defaultSimulationTermId,
  }); // セット数ありのものだけ
  const { chartStats } = useCalculatingChartData({ strategyList, defaultSets: 1 });
  return roundUpBy1000(chartStats.marginRequired);
};

export const useOmakasePlanSimulationChart = () => {
  const dispatch = useDispatch();
  const termOptions = useSelector((state) => state.constants.selectionTermOptions);
  const selectedTermId = useSelector((state) => state.omakasePlan.creating.selectedSimulationTermId);
  const strategyList = useMargeOmakasePlanStrategiesSetCount({ filtering: true }); // セット数ありのものだけ
  const { chartData, chartStats } = useCalculatingChartData({ strategyList, defaultSets: 1 });
  const defaultMarginRequired = useOmakasePlanDefaultMarginRequired();
  const handleChange = useCallback(
    (value) => {
      dispatch(changeOmakaseCreatingSimulationTermId({ simulationTermId: value }));
    },
    [dispatch],
  );
  return useMemo(
    () => ({
      termOptions,
      selectedTermId,
      onChange: handleChange,
      chartData,
      simulationData: {
        totalPl: chartStats.totalPl,
        // marginRecommended: roundUpBy1000(chartStats.marginRecommended),
        marginRequired: defaultMarginRequired,
        roi: calcRoi(chartStats),
      },
      strategyList,
    }),
    [selectedTermId, termOptions, handleChange, chartData, chartStats, defaultMarginRequired, strategyList],
  );
};

const findStrategy = (strategyList, selectedStrategyId) =>
  strategyList.find((strategy) => strategy.strategyId === selectedStrategyId);

export const strategyOptionsMapper = {
  toValueLabel: (strategy) => ({
    value: strategy.strategyId,
    label: strategy.strategyDetail.name,
  }),
  toIdValue: (strategy) => ({
    id: strategy.strategyId,
    value: strategy.strategyDetail.name,
  }),
};

export const useOmakasePlanLogicSettings = ({ mapper = strategyOptionsMapper.toValueLabel } = {}) => {
  const dispatch = useDispatch();
  const strategyList = useMargeOmakasePlanStrategiesSetCount({ filtering: true }); // セット数ありのものだけ
  const selectedStrategyId = useSelector((state) => state.omakasePlan.creating.selectedStrategyId);
  const selectedStrategy = findStrategy(strategyList, selectedStrategyId);
  const strategiesOptions = useMemo(() => strategyList.map(mapper), [strategyList, mapper]);
  const handleChangeStrategy = useCallback(
    (value) => {
      dispatch(changeOmakaseCreatingSelectedStrategyId({ strategyId: value }));
    },
    [dispatch],
  );

  const tableDataMain = useSelectionsTableDataMain({ strategyList, defaultSets: 1 }); // autSelectの関数を再利用

  useEffect(() => {
    // 選択中のstrategyが削除された場合
    if (selectedStrategy == null && selectedStrategyId != null) {
      dispatch(changeOmakaseCreatingSelectedStrategyId({ strategyId: strategyList[0]?.strategyId || null }));
    }
  }, [dispatch, selectedStrategy, selectedStrategyId, strategyList]);

  return useMemo(
    () => ({
      strategyOptions: {
        items: strategiesOptions,
        onChange: handleChangeStrategy,
        selected: selectedStrategyId,
      },
      selectedStrategy,
      ...tableDataMain,
      /**
       * tableDataMain = {
       *   simulationTradesData,
       *   autoTradeSettingsData,
       *   orderGroupsData,
       *   helpers: {
       *    getQuantity,
       *    getStrategySets,
       *    getCommissionOrDividend,
       *    getRealizedProfitLoss,
       *    getSwapInterest,
       *   },
       * }
       */
    }),
    [strategiesOptions, handleChangeStrategy, selectedStrategyId, selectedStrategy, tableDataMain],
  );
};

// AutoSelectとおまかせのどちらかを使用する termId=null,isOmakase=true / termId=?,isOmakase=false
export const useSelectionsTableDataSwitch = (termId, isOmakase = false) => {
  // AutoSelect用
  const autoSelectSelectionModalInfo = useSelector((state) => state.autoSelect.selectionModalInfo);
  // おまかせ用
  const omakase = useMargeOmakasePlanStrategiesSetCount({ filtering: true }); // セット数ありのものだけ
  // どちらかを使用
  const [strategyList, defaultSets] = isOmakase
    ? [omakase, 1]
    : [autoSelectSelectionModalInfo[termId]?.strategyList, autoSelectSelectionModalInfo[termId]?.defaultSets];
  return useSelectionsTableDataMain({ strategyList, defaultSets });
};

export const getOmakasePiceChartUri = () => `${MOBILE_AUTO_SELECT_PRICE_CHART_URL}?isMobile=true&isOmakase=true`;

// 別Window、WebView用
export const useExternalOmakasePlanData = (isOmakase) => {
  const strategyList = useMargeOmakasePlanStrategiesSetCount({ filtering: true });
  const inputSetCount = useSelector((state) => state.omakasePlan.creating.inputSetCount);
  return useMemo(() => (isOmakase ? { strategyList, inputSetCount } : null), [isOmakase, strategyList, inputSetCount]);
};

// OMS側からのエラーコードを文字に変換するメソッド
// 全失敗、一部失敗の場合が分けられています
export const convertErrorCodeToText = ({ type, failedList, succeededList }) => {
  const errorCodeOrder = ['WI-10020', 'SA-17002', 'SA-17009', 'WI-10021', 'CA-00062', 'SA-12617'];
  const sortedListByCode = failedList.sort((a, b) => {
    const indexA = errorCodeOrder.indexOf(a.errCode);
    const indexB = errorCodeOrder.indexOf(b.errCode);
    const safeIndexA = indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA;
    const safeIndexB = indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB;
    return safeIndexA - safeIndexB;
  });

  if (type === 'all') {
    const errorCodes = new Set(sortedListByCode.map((item) => item.errCode));
    const errorsInfo = Array.from(errorCodes).map((code) => {
      const { summary, url } = getRunNowErrorMessageByErrorCode(FX, code);
      return { summary, url };
    });

    return {
      title: RUN_NOW_ERROR_MESSAGES.allErrorTitle,
      info: errorsInfo,
    };
  }
  if (type === 'partial') {
    const fxFailedList = [];
    sortedListByCode.forEach((item) => {
      if (item.status === STRATEGY_SETS_RESULTS.FAIL && item.errCode) {
        const errReason = getRunNowErrorMessageByErrorCode(FX, item.errCode).short;
        fxFailedList.push({ ...item, errReason });
      }
    });
    return {
      title: RUN_NOW_ERROR_MESSAGES.partialErrorTitle,
      info: RUN_NOW_ERROR_MESSAGES.partialErrorText,
      strategyResults: [...succeededList, ...fxFailedList],
    };
  }
  return null;
};

export const useOmakasePlanRunConfirm = ({
  successCallback, // args => none.
  failureCallback, // args => {type='all'/'partial', failedList }
  marginErrorCallback, // args => {currentOrderableMargin, marginRequired, shortfall}
}) => {
  const dispatch = useDispatch();
  const [name, setName] = useState(AP_GROUP_SOURCES.OMAKASE.NAME); // #52 デフォルトで表示されているポートフォリオ名は「オートセレクト」
  const [error, setError] = useState(null);
  const strategyList = useMargeOmakasePlanStrategiesSetCount({ filtering: true }); // セット数ありのものだけ
  const {
    chartStats: { marginRequiredFX },
  } = useCalculatingChartData({ strategyList, defaultSets: 1 });
  const orderableMarginFX = useSummaryInfoByServiceId(FX)?.orderableMargin || 0;
  const checkMargin = useCallback(() => {
    const shortfall = orderableMarginFX - marginRequiredFX;
    if (shortfall < 0) {
      marginErrorCallback?.({
        currentOrderableMargin: orderableMarginFX, // 現在の発注可能額
        marginRequired: marginRequiredFX, // 発注証拠金
        shortfall: Math.abs(shortfall), // 不足額
      });
      return false;
    }
    return true;
  }, [marginRequiredFX, orderableMarginFX, marginErrorCallback]);

  const validateName = useCallback((value) => {
    if (!value || value.trim() === '') {
      setError('ポートフォリオ名の指定は必須です。'); // TODO: constants
      return false;
    }
    if (value && value.length > 40) {
      setError('ポートフォリオ名は40文字以内でご入力ください。'); // TODO: constants
      return false;
    }
    setError(null);
    return true;
  }, []);
  const isProcessing = useSelector((state) => state.omakasePlan.submit.isProcessing);

  const runNowHandle = useCallback(() => {
    if (checkMargin()) {
      dispatch(createOmakasePlan({ serviceId: FX, name, strategyList, successCallback, failureCallback }));
    }
  }, [dispatch, name, strategyList, successCallback, failureCallback, checkMargin]);

  return useMemo(
    () => ({
      nameInput: {
        get: name,
        set: setName,
        validation: validateName,
        name: 'name',
        error: error ? [createErrorObj('name', error)] : [],
        reset: () => setName(AP_GROUP_SOURCES.OMAKASE.NAME),
      },
      strategyList,
      runNowBtn: {
        isDisabled: !!error,
        onClick: runNowHandle,
        label: OMAKASE_PLAN_START_TEXT,
      },
      isProcessing,
    }),
    [name, validateName, error, strategyList, runNowHandle, isProcessing],
  );
};
